import { useEffect, useState, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Sparkles,
  CalendarDays,
  ShieldAlert,
  Megaphone,
  ClipboardSignature,
  Copy,
  RefreshCw,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { getReport } from "../lib/api";
import { track } from "../lib/track";

const REPORTS = [
  {
    kind: "week",
    label: "What I built this week",
    desc: "5-second recap of shipped changes and themes.",
    Icon: CalendarDays,
    accent: "from-blue-500 to-indigo-500",
  },
  {
    kind: "bugs",
    label: "Where bugs are likely",
    desc: "Hotspots ranked by edit frequency and risk.",
    Icon: ShieldAlert,
    accent: "from-rose-500 to-orange-500",
  },
  {
    kind: "pitch",
    label: "Explain my project in 30 sec",
    desc: "One-liner, stack, and core loop — for friends.",
    Icon: Megaphone,
    accent: "from-emerald-500 to-teal-500",
  },
  {
    kind: "handoff",
    label: "Handoff brief",
    desc: "What's in flight, files to know, first task.",
    Icon: ClipboardSignature,
    accent: "from-violet-500 to-fuchsia-500",
  },
];

export default function TraceReports({ projectId, projectName }) {
  const [open, setOpen] = useState(null); // selected kind or null
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skipped, setSkipped] = useState(null);

  const load = useCallback(
    async (kind, refresh = false) => {
      setLoading(true);
      setReport(null);
      setSkipped(null);
      try {
        const data = await getReport(projectId, kind, refresh);
        if (data.skipped) {
          setSkipped(data.reason || "Not enough data yet.");
        } else {
          setReport(data.report);
        }
        track(refresh ? "Report Refreshed" : "Report Opened", {
          props: { kind, project: projectName },
        });
      } catch (e) {
        toast.error(
          e?.response?.data?.detail || "Could not generate report. Try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [projectId, projectName]
  );

  useEffect(() => {
    if (open) load(open);
  }, [open, load]);

  const meta = open ? REPORTS.find((r) => r.kind === open) : null;

  return (
    <section className="mt-12" data-testid="trace-reports">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={14} strokeWidth={2.4} className="text-indigo-500" />
        <span className="text-[11px] uppercase tracking-[0.18em] text-indigo-700 font-bold">
          Trace Reports · AI
        </span>
      </div>
      <h2 className="text-xl tracking-tight text-[#0b1220] font-semibold">
        Memory that does the thinking for you.
      </h2>
      <p className="text-[13px] text-[#4b5563] mt-1">
        One click and Claude reads your week.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {REPORTS.map((r) => (
          <button
            key={r.kind}
            data-testid={`report-card-${r.kind}`}
            onClick={() => setOpen(r.kind)}
            className="group relative text-left rounded-2xl bg-white border border-[#e5e7eb] p-5 hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(17,24,39,0.06)] hover:border-indigo-200 transition-all duration-300 overflow-hidden"
          >
            <div
              className={`absolute -top-12 -right-12 w-36 h-36 rounded-full opacity-10 bg-gradient-to-br ${r.accent} blur-2xl pointer-events-none group-hover:opacity-20 transition`}
            />
            <div className="flex items-start gap-3">
              <span
                className={`shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br ${r.accent} flex items-center justify-center shadow-sm`}
              >
                <r.Icon size={16} strokeWidth={2.4} className="text-white" />
              </span>
              <div className="min-w-0">
                <div className="text-[14.5px] font-semibold text-[#0b1220] flex items-center gap-1.5">
                  {r.label}
                  <ArrowRight
                    size={13}
                    className="text-[#9ca3af] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-indigo-500"
                  />
                </div>
                <div className="text-[12.5px] text-[#4b5563] mt-1 leading-relaxed">
                  {r.desc}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[520px] bg-white border-l border-[#e5e7eb] overflow-y-auto"
          data-testid="report-sheet"
        >
          {meta && (
            <SheetHeader>
              <div className="flex items-center gap-2">
                <span
                  className={`w-7 h-7 rounded-lg bg-gradient-to-br ${meta.accent} flex items-center justify-center shadow-sm`}
                >
                  <meta.Icon size={14} strokeWidth={2.4} className="text-white" />
                </span>
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-700 font-bold">
                  Trace Report
                </span>
              </div>
              <SheetTitle className="text-[20px] tracking-tight text-[#0b1220] mt-2">
                {meta.label}
              </SheetTitle>
              <SheetDescription className="text-[12.5px] text-[#4b5563]">
                {meta.desc}
              </SheetDescription>
            </SheetHeader>
          )}

          <div className="mt-5">
            {loading && (
              <div className="flex items-center gap-2 text-[#4b5563] text-sm">
                <Loader2 size={14} className="animate-spin" />
                Reading your project memory…
              </div>
            )}
            {!loading && skipped && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-[13px] text-[#7c2d12] leading-relaxed">
                {skipped}
              </div>
            )}
            {!loading && report && open && (
              <ReportBody kind={open} report={report} />
            )}
          </div>

          {!loading && report && open && (
            <div className="mt-6 flex items-center gap-2">
              <button
                data-testid="report-copy-btn"
                onClick={() => copyReport(open, report)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium text-[#1f2937] bg-white border border-[#e5e7eb] hover:bg-[#f7f8fa] transition"
              >
                <Copy size={13} strokeWidth={2.2} /> Copy as markdown
              </button>
              <button
                data-testid="report-refresh-btn"
                onClick={() => load(open, true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 transition"
              >
                <RefreshCw size={13} strokeWidth={2.4} /> Regenerate
              </button>
              {report.generated_at && (
                <span className="ml-auto text-[10.5px] text-[#9ca3af]">
                  {new Date(report.generated_at).toLocaleString()}
                </span>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}

/* ---------- Renderers per kind ---------- */
function Section({ label, children }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#4b5563] font-semibold">
        {label}
      </div>
      <div className="mt-1.5 text-[14px] text-[#0b1220] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-[#f3f4f6] text-[#1f2937]",
    high: "bg-rose-50 text-rose-700 border border-rose-200",
    medium: "bg-amber-50 text-amber-800 border border-amber-200",
    low: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function ReportBody({ kind, report }) {
  if (kind === "week") {
    return (
      <div data-testid="report-week">
        <h3 className="text-[22px] tracking-tight text-[#0b1220] font-semibold leading-snug">
          <span className="gradient-text">{report.headline}</span>
        </h3>
        <Section label="Highlights">
          <ul className="space-y-1.5 list-disc list-inside marker:text-indigo-400">
            {(report.highlights || []).map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </Section>
        {report.themes?.length > 0 && (
          <Section label="Themes">
            <div className="flex flex-wrap gap-1.5">
              {report.themes.map((t, i) => <Pill key={i}>{t}</Pill>)}
            </div>
          </Section>
        )}
        {report.next_week && (
          <Section label="Next week, focus on">
            <p className="flex items-start gap-1.5">
              <ArrowRight size={13} className="text-indigo-500 mt-1 shrink-0" />
              <span>{report.next_week}</span>
            </p>
          </Section>
        )}
      </div>
    );
  }

  if (kind === "bugs") {
    return (
      <div data-testid="report-bugs">
        <p className="text-[15px] text-[#0b1220] leading-relaxed mb-4">
          {report.summary}
        </p>
        <Section label="Hotspots">
          <ul className="space-y-3">
            {(report.hotspots || []).map((h, i) => (
              <li key={i} className="rounded-lg border border-[#e5e7eb] p-3 bg-white">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <code className="font-mono text-[13px] text-[#0b1220]">{h.file}</code>
                  <Pill tone={h.risk}>{h.risk} risk</Pill>
                </div>
                <p className="text-[12.5px] text-[#4b5563] mt-1.5">{h.why}</p>
              </li>
            ))}
          </ul>
        </Section>
        {report.suggestion && (
          <Section label="Suggested check">
            <p className="flex items-start gap-1.5">
              <ArrowRight size={13} className="text-indigo-500 mt-1 shrink-0" />
              <span>{report.suggestion}</span>
            </p>
          </Section>
        )}
      </div>
    );
  }

  if (kind === "pitch") {
    return (
      <div data-testid="report-pitch">
        <h3 className="text-[20px] sm:text-[22px] tracking-tight text-[#0b1220] font-semibold leading-snug">
          <span className="gradient-text">{report.one_liner}</span>
        </h3>
        <Section label="Stack">
          <div className="flex flex-wrap gap-1.5">
            {(report.stack || []).map((s, i) => <Pill key={i}>{s}</Pill>)}
          </div>
        </Section>
        <Section label="Core loop">
          <p>{report.core_loop}</p>
        </Section>
        {report.stage && (
          <Section label="Stage">
            <Pill>{report.stage}</Pill>
          </Section>
        )}
      </div>
    );
  }

  if (kind === "handoff") {
    return (
      <div data-testid="report-handoff">
        <Section label="Context">
          <p>{report.context}</p>
        </Section>
        <Section label="Current focus">
          <p className="font-semibold text-[#0b1220]">{report.current_focus}</p>
        </Section>
        {report.open_threads?.length > 0 && (
          <Section label="Open threads">
            <ul className="space-y-2.5">
              {report.open_threads.map((t, i) => (
                <li key={i} className="rounded-lg border border-[#e5e7eb] p-3 bg-white">
                  <div className="text-[13.5px] font-semibold text-[#0b1220]">{t.title}</div>
                  <div className="text-[12.5px] text-[#4b5563] mt-1">{t.detail}</div>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {report.files_to_know?.length > 0 && (
          <Section label="Files to know">
            <div className="flex flex-wrap gap-1.5">
              {report.files_to_know.map((f, i) => (
                <code key={i} className="font-mono text-[12px] px-2 py-0.5 rounded bg-[#f3f4f6] text-[#0b1220]">
                  {f}
                </code>
              ))}
            </div>
          </Section>
        )}
        {report.first_task && (
          <Section label="First task tomorrow">
            <p className="flex items-start gap-1.5">
              <ArrowRight size={13} className="text-indigo-500 mt-1 shrink-0" />
              <span>{report.first_task}</span>
            </p>
          </Section>
        )}
      </div>
    );
  }

  return null;
}

/* ---------- Markdown serialization for "Copy" ---------- */
function asMarkdown(kind, r) {
  if (kind === "week") {
    return [
      `# This week — ${r.headline}`,
      "",
      "## Highlights",
      ...(r.highlights || []).map((h) => `- ${h}`),
      r.themes?.length ? `\n**Themes:** ${r.themes.join(", ")}` : "",
      r.next_week ? `\n**Next week:** ${r.next_week}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }
  if (kind === "bugs") {
    return [
      "# Bug hotspots",
      "",
      r.summary,
      "",
      "## Files",
      ...(r.hotspots || []).map((h) => `- **${h.file}** (${h.risk}) — ${h.why}`),
      r.suggestion ? `\n**Suggestion:** ${r.suggestion}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }
  if (kind === "pitch") {
    return [
      `# ${r.one_liner}`,
      "",
      `**Stack:** ${(r.stack || []).join(", ")}`,
      `**Core loop:** ${r.core_loop}`,
      r.stage ? `**Stage:** ${r.stage}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }
  if (kind === "handoff") {
    return [
      "# Handoff brief",
      "",
      `**Context:** ${r.context}`,
      `**Current focus:** ${r.current_focus}`,
      "",
      "## Open threads",
      ...(r.open_threads || []).map((t) => `- **${t.title}** — ${t.detail}`),
      "",
      `**Files to know:** ${(r.files_to_know || []).join(", ")}`,
      r.first_task ? `\n**First task:** ${r.first_task}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }
  return JSON.stringify(r, null, 2);
}

async function copyReport(kind, report) {
  try {
    await navigator.clipboard.writeText(asMarkdown(kind, report));
    track("Report Copied", { props: { kind } });
    toast.success("Copied — paste into Slack, Notion, or your README.");
  } catch {
    toast.error("Could not copy. Select & copy manually.");
  }
}
